import { faCheckCircle, faComments } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import lowerFirst from "lodash-es/lowerFirst";
import React, { useEffect, useMemo, useState } from "react";

import MessageForm, { MessageFormData } from "@/mk2/apps/bazaar/components/MessageForm";
import { ContactSellerActionsStep1 } from "@/mk2/apps/bazaar/containers/ProductDetail/ContactSellerActionsStep1";
import { ContactSellerActionsStep2 } from "@/mk2/apps/bazaar/containers/ProductDetail/ContactSellerActionsStep2";
import { CONTACT_SELLER_FORM } from "@/mk2/apps/bazaar/helpers";
import { BazaarMessageEntity, ProductEntity, ProductQuality } from "@/mk2/apps/bazaar/schemas";
import { AvatarImg } from "@/mk2/components/AvatarImg";
import { Btn, BtnSidePadding, BtnType } from "@/mk2/components/Btn";
import useEventCallback from "@/mk2/hooks/useEventCallback";
import { UserEntity } from "@/mk2/schemas";
import {
    BZ2_CONTACT_MESSAGE_DIALOG_OPENER,
    BZ2_CONTACT_MESSAGE_WAS_SENT,
    BZ2_JUST_SEND_MESSAGE,
    BZ2_SEND_MESSAGE,
    BZ_CONTACT_SEND_NEXT_MESSAGE,
    BZ_CONTACT_SHOW_COMMUNICATION_WITH_SELLER,
} from "@/mk/autogenerated/translation";
import { bazaar_redirect_to_eshop_url } from "@/mk/urls/functions";

import styles from "./ContactSellerForBuyer.mscss";

interface OwnProps {
    product: ProductEntity;
    canSendMsg: boolean;
    sellerPhone: string;
    eshopUrl: string;

    buyerUser: UserEntity;
    buyerMessages?: BazaarMessageEntity[];

    hasMessageFormInitiallyOpen: boolean;
    onSendMessage(productId: number, productSlug: string, values: MessageFormData);
    onScrollToMessages(event: React.MouseEvent<HTMLAnchorElement>);
}

export const ContactSellerForBuyer: React.FunctionComponent<OwnProps> = ({
    product,
    sellerPhone,
    eshopUrl,
    canSendMsg,

    buyerUser,
    buyerMessages = null,
    hasMessageFormInitiallyOpen,

    onSendMessage,
    onScrollToMessages,
}) => {
    const [showMessagForm, setShowMessageForm] = useState<boolean>(hasMessageFormInitiallyOpen);

    const hasMessage = buyerMessages && buyerMessages.length > 0;
    const hasRecentMessage =
        hasMessage &&
        Date.now() - new Date(buyerMessages[buyerMessages.length - 1].sentDate).getTime() <
            5 * 60 * 1000; // 5min

    const handleOnSendMessage = useEventCallback(
        (values: MessageFormData) => {
            onSendMessage(product.id, product.slug, values);
        },
        [product, onSendMessage, setShowMessageForm],
    );

    useEffect(() => {
        // zavri message form, ak sa nam uspesne podarilo
        // pridat spravu (neboli form errors)
        if (hasRecentMessage && showMessagForm) {
            setShowMessageForm(false);
        }
    }, [hasRecentMessage, showMessagForm, setShowMessageForm]);

    const onCloseMessageForm = useEventCallback(() => {
        setShowMessageForm(false);
    }, [setShowMessageForm]);

    const onOpenMessageForm = useEventCallback(() => {
        setShowMessageForm(true);
    }, [setShowMessageForm]);

    const initialValues: MessageFormData = useMemo(
        () => ({
            message: "",
            photos: [],
        }),
        [],
    );

    // poradie if-ov je dolezite, nie su mutually exclusive
    if (showMessagForm) {
        return (
            <div className={styles.ContactSellerForBuyer__messageForm}>
                <div className={styles.ContactSellerForBuyer__messageFormIntro}>
                    {BZ2_CONTACT_MESSAGE_DIALOG_OPENER}
                </div>
                <div className={styles.ContactSellerForBuyer__messageFormContainer}>
                    <AvatarImg
                        className={styles.ContactSellerForBuyer__messageFormAvatar}
                        user={buyerUser}
                        size={32}
                    />
                    <MessageForm
                        className={styles.ContactSellerForBuyer__messageFormInnerForm}
                        form={CONTACT_SELLER_FORM(product.id)}
                        initialValues={initialValues}
                        onCancel={onCloseMessageForm}
                        onSave={handleOnSendMessage}
                    />
                </div>
            </div>
        );
    } else if (hasMessage) {
        if (hasRecentMessage) {
            const showMsgsBtn = (
                <Btn
                    type={BtnType.Link}
                    sidePadding={BtnSidePadding.None}
                    label={BZ_CONTACT_SEND_NEXT_MESSAGE}
                    onClick={onScrollToMessages}
                />
            );

            return (
                <div className={styles.ContactSellerForBuyer__messageSent}>
                    <FontAwesomeIcon
                        icon={faCheckCircle}
                        className={styles.ContactSellerForBuyer__messageSentIcon}
                    />
                    <p className={styles.ContactSellerForBuyer__messageSentText}>
                        {BZ2_CONTACT_MESSAGE_WAS_SENT}
                    </p>

                    <ContactSellerActionsStep2 showMsgsBtn={showMsgsBtn} inline />
                </div>
            );
        } else {
            const showMsgsBtn = (
                <Btn
                    type={BtnType.BlueOutline}
                    label={BZ_CONTACT_SHOW_COMMUNICATION_WITH_SELLER}
                    onClick={onScrollToMessages}
                />
            );

            return (
                <>
                    <div className={styles.ContactSellerForBuyer}>
                        <ContactSellerActionsStep2 showMsgsBtn={showMsgsBtn} />
                    </div>
                </>
            );
        }
    } else {
        const sendMsgBtnLabel = [ProductQuality.SERVICES, ProductQuality.JOBS].includes(
            product.quality,
        )
            ? BZ2_JUST_SEND_MESSAGE
            : BZ2_SEND_MESSAGE;
        const sendMsgBtn = canSendMsg ? (
            <Btn
                type={BtnType.Blue}
                label={sendMsgBtnLabel}
                onClick={onOpenMessageForm}
                icon={faComments}
            />
        ) : null;

        const phoneLink = sellerPhone ? (
            <Btn
                type={BtnType.Link}
                label={sellerPhone}
                link={`tel:${sellerPhone}`}
                linkTarget="_blank"
                linkRel="noopener"
                sidePadding={BtnSidePadding.None}
                asAnchor
            />
        ) : null;

        const eshopLink = eshopUrl ? (
            <Btn
                type={BtnType.Link}
                label={eshopUrl.replace(/^https?:\/\//, "").split("/")[0]}
                link={bazaar_redirect_to_eshop_url(product.id)}
                linkTarget="_blank"
                linkRel="noopener"
                sidePadding={BtnSidePadding.None}
                asAnchor
            />
        ) : null;

        return (
            <ContactSellerActionsStep1
                sendMsgBtn={sendMsgBtn}
                phoneLink={phoneLink}
                eshopLink={eshopLink}
            />
        );
    }
};
